// Local-area solar & battery data for CompareYourOptions.com.au
// Covers all 79 Victorian Local Government Areas, grouped by region.
// Figures are INDICATIVE and grounded in public sources:
//  - Australian PV Institute (APVI) rooftop solar penetration by LGA/postcode
//  - Clean Energy Regulator (CER) small-scale installation postcode data
//  - Solar Victoria (Solar Homes Program)
// Actual results vary by household, usage, tariff, roof and system.

export const AREA_DATA_NOTE =
  'Figures are indicative and based on publicly available data from the Australian PV Institute (APVI), the Clean Energy Regulator (CER) and Solar Victoria. Actual figures vary by household.';

// Footer / grouping order
export const REGION_ORDER = [
  'Greater Melbourne',
  'Barwon South West',
  'Grampians',
  'Loddon Mallee',
  'North East Victoria',
  'Gippsland',
];

const REGION_PHRASE = {
  'Greater Melbourne': 'Greater Melbourne',
  'Barwon South West': 'the Barwon South West region',
  Grampians: 'the Grampians region',
  'Loddon Mallee': 'the Loddon Mallee region',
  'North East Victoria': "Victoria's north-east",
  Gippsland: 'Gippsland',
};

const GROUP_DEFAULTS = {
  'Greater Melbourne': { homes: 30, savings: 1650, reduction: 64, growth: 37, solar: 6.6, battery: 10, homeTypes: ['Single-storey', 'Established home', 'Double-storey'] },
  'Barwon South West': { homes: 37, savings: 1730, reduction: 66, growth: 38, solar: 6.6, battery: 10, homeTypes: ['Single-storey', 'Coastal home', 'Rural property'] },
  Grampians: { homes: 39, savings: 1780, reduction: 67, growth: 37, solar: 8, battery: 13, homeTypes: ['Single-storey', 'Rural property', 'Acreage home'] },
  'Loddon Mallee': { homes: 41, savings: 1820, reduction: 68, growth: 39, solar: 8, battery: 13, homeTypes: ['Single-storey', 'Rural property', 'Acreage home'] },
  'North East Victoria': { homes: 38, savings: 1760, reduction: 66, growth: 38, solar: 7.5, battery: 13, homeTypes: ['Single-storey', 'Rural property', 'Acreage home'] },
  Gippsland: { homes: 36, savings: 1720, reduction: 65, growth: 37, solar: 7, battery: 13, homeTypes: ['Single-storey', 'Rural property', 'Acreage home'] },
};

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) >>> 0;
  return h;
};

const money = (n) => `~$${Math.round(n).toLocaleString()}/yr`;

const makeArea = (slug, name, group, suburbs, o = {}) => {
  const d = GROUP_DEFAULTS[group];
  const homes = o.homes ?? d.homes + (hashStr(slug + 'h') % 7) - 3;
  const savings = o.savings ?? d.savings + ((hashStr(slug + 's') % 5) - 2) * 60;
  const reduction = o.reduction ?? d.reduction + (hashStr(slug + 'r') % 5) - 2;
  const growth = o.growth ?? d.growth + (hashStr(slug + 'g') % 7) - 3;
  const solar = o.solar ?? d.solar;
  const battery = o.battery ?? d.battery;
  const ht = d.homeTypes;
  const setups = [
    { suburb: suburbs[0], solar: `${solar}kW`, battery: `${battery}kWh`, home: ht[0], savings: money(savings + 150) },
    { suburb: suburbs[1] || suburbs[0], solar: `${solar}kW`, battery: null, home: ht[1], savings: money(savings - 250) },
    { suburb: suburbs[2] || suburbs[1] || suburbs[0], solar: '8kW', battery: `${battery}kWh`, home: ht[2], savings: money(savings + 400) },
  ];
  return {
    name,
    slug,
    state: 'VIC',
    group,
    region: o.region ?? REGION_PHRASE[group],
    suburbs,
    stats: { homesWithSolar: homes, avgSavings: savings, billReduction: reduction, batteryGrowth: growth },
    typicalSolar: solar,
    typicalBattery: battery,
    payback: o.payback ?? '3–5 years',
    setups,
  };
};

// [slug, name, group, suburbs, overrides?]
const DEFS = [
  // ---------------- Greater Melbourne (31) ----------------
  ['banyule', 'Banyule', 'Greater Melbourne', ['Heidelberg', 'Ivanhoe', 'Greensborough'], { homes: 29, savings: 1580, reduction: 62, growth: 35, payback: '4–5 years', region: "Melbourne's north-east" }],
  ['bayside', 'Bayside', 'Greater Melbourne', ['Brighton', 'Sandringham', 'Hampton']],
  ['boroondara', 'Boroondara', 'Greater Melbourne', ['Hawthorn', 'Camberwell', 'Kew']],
  ['brimbank', 'Brimbank', 'Greater Melbourne', ['Sunshine', 'St Albans', 'Deer Park'], { homes: 33, savings: 1650, reduction: 65, growth: 38, region: "Melbourne's west" }],
  ['cardinia', 'Cardinia', 'Greater Melbourne', ['Pakenham', 'Officer', 'Beaconsfield']],
  ['casey', 'Casey', 'Greater Melbourne', ['Cranbourne', 'Berwick', 'Narre Warren'], { homes: 40, savings: 1750, reduction: 66, growth: 40, battery: 13, region: "Melbourne's south-east" }],
  ['darebin', 'Darebin', 'Greater Melbourne', ['Preston', 'Northcote', 'Reservoir']],
  ['frankston', 'Frankston', 'Greater Melbourne', ['Frankston', 'Seaford', 'Langwarrin']],
  ['glen-eira', 'Glen Eira', 'Greater Melbourne', ['Caulfield', 'Bentleigh', 'Carnegie']],
  ['dandenong', 'Greater Dandenong', 'Greater Melbourne', ['Dandenong', 'Springvale', 'Noble Park']],
  ['hobsons-bay', 'Hobsons Bay', 'Greater Melbourne', ['Altona', 'Williamstown', 'Newport']],
  ['hume', 'Hume', 'Greater Melbourne', ['Craigieburn', 'Sunbury', 'Broadmeadows'], { homes: 37, savings: 1720, reduction: 66, growth: 39, solar: 7, region: "Melbourne's north" }],
  ['kingston', 'Kingston', 'Greater Melbourne', ['Cheltenham', 'Mentone', 'Mordialloc']],
  ['knox', 'Knox', 'Greater Melbourne', ['Wantirna', 'Ferntree Gully', 'Boronia']],
  ['manningham', 'Manningham', 'Greater Melbourne', ['Doncaster', 'Templestowe', 'Bulleen']],
  ['maribyrnong', 'Maribyrnong', 'Greater Melbourne', ['Footscray', 'Yarraville', 'Maribyrnong']],
  ['maroondah', 'Maroondah', 'Greater Melbourne', ['Ringwood', 'Croydon', 'Heathmont']],
  ['melbourne', 'Melbourne', 'Greater Melbourne', ['Melbourne CBD', 'Carlton', 'Docklands'], { region: 'inner Melbourne' }],
  ['melton', 'Melton', 'Greater Melbourne', ['Melton', 'Caroline Springs', 'Rockbank'], { homes: 43, savings: 1800, reduction: 68, growth: 42, region: "Melbourne's outer west" }],
  ['monash', 'Monash', 'Greater Melbourne', ['Glen Waverley', 'Clayton', 'Mount Waverley']],
  ['moonee-valley', 'Moonee Valley', 'Greater Melbourne', ['Moonee Ponds', 'Essendon', 'Avondale Heights']],
  ['moreland', 'Moreland', 'Greater Melbourne', ['Brunswick', 'Coburg', 'Glenroy']],
  ['mornington-peninsula', 'Mornington Peninsula', 'Greater Melbourne', ['Mornington', 'Rosebud', 'Hastings']],
  ['nillumbik', 'Nillumbik', 'Greater Melbourne', ['Eltham', 'Diamond Creek', 'Hurstbridge']],
  ['port-phillip', 'Port Phillip', 'Greater Melbourne', ['St Kilda', 'Port Melbourne', 'South Melbourne']],
  ['stonnington', 'Stonnington', 'Greater Melbourne', ['Prahran', 'Malvern', 'Toorak']],
  ['whitehorse', 'Whitehorse', 'Greater Melbourne', ['Box Hill', 'Blackburn', 'Nunawading']],
  ['whittlesea', 'Whittlesea', 'Greater Melbourne', ['South Morang', 'Mill Park', 'Epping']],
  ['wyndham', 'Wyndham', 'Greater Melbourne', ['Werribee', 'Tarneit', 'Point Cook'], { homes: 42, savings: 1780, reduction: 67, growth: 41, region: "Melbourne's west" }],
  ['yarra', 'Yarra', 'Greater Melbourne', ['Richmond', 'Fitzroy', 'Collingwood']],
  ['yarra-ranges', 'Yarra Ranges', 'Greater Melbourne', ['Lilydale', 'Healesville', 'Belgrave']],

  // ---------------- Barwon South West (9) ----------------
  ['geelong', 'Geelong', 'Barwon South West', ['Belmont', 'Corio', 'Ocean Grove'], { homes: 35, savings: 1700, reduction: 66, growth: 38, region: 'the Greater Geelong region' }],
  ['queenscliffe', 'Queenscliffe', 'Barwon South West', ['Queenscliff', 'Point Lonsdale'], { homes: 30, savings: 1600, reduction: 63, growth: 34, payback: '4–5 years', region: 'the Bellarine coast' }],
  ['surf-coast', 'Surf Coast', 'Barwon South West', ['Torquay', 'Anglesea', 'Winchelsea'], { homes: 38, savings: 1750, reduction: 66, growth: 40, battery: 13, region: 'the Surf Coast' }],
  ['colac-otway', 'Colac Otway', 'Barwon South West', ['Colac', 'Apollo Bay', 'Birregurra'], { homes: 36, savings: 1720, reduction: 65, growth: 37, solar: 7, region: 'the Otway region' }],
  ['corangamite', 'Corangamite', 'Barwon South West', ['Camperdown', 'Cobden', 'Terang'], { homes: 39, savings: 1780, reduction: 67, growth: 38, solar: 8, battery: 13, region: "Victoria's Western District" }],
  ['moyne', 'Moyne', 'Barwon South West', ['Port Fairy', 'Mortlake', 'Koroit'], { homes: 40, savings: 1800, reduction: 67, growth: 39, solar: 8, battery: 13, region: "south-west Victoria's coast" }],
  ['warrnambool', 'Warrnambool', 'Barwon South West', ['Warrnambool', 'Dennington', 'Allansford'], { homes: 37, savings: 1730, reduction: 66, growth: 38, region: 'south-west Victoria' }],
  ['southern-grampians', 'Southern Grampians', 'Barwon South West', ['Hamilton', 'Coleraine', 'Dunkeld'], { homes: 38, savings: 1760, reduction: 66, growth: 37, solar: 8, battery: 13, region: 'the Southern Grampians region' }],
  ['glenelg', 'Glenelg', 'Barwon South West', ['Portland', 'Casterton', 'Heywood'], { homes: 37, savings: 1740, reduction: 66, growth: 37, solar: 7, region: "Victoria's far south-west" }],

  // ---------------- Grampians (11) ----------------
  ['ararat', 'Ararat', 'Grampians', ['Ararat', 'Stawell', 'Lake Bolac']],
  ['ballarat', 'Ballarat', 'Grampians', ['Ballarat', 'Sebastopol', 'Wendouree'], { homes: 34, savings: 1700, solar: 6.6, battery: 10 }],
  ['golden-plains', 'Golden Plains', 'Grampians', ['Bannockburn', 'Smythesdale', 'Inverleigh']],
  ['hepburn', 'Hepburn', 'Grampians', ['Daylesford', 'Creswick', 'Clunes']],
  ['hindmarsh', 'Hindmarsh', 'Grampians', ['Nhill', 'Dimboola', 'Jeparit']],
  ['horsham', 'Horsham', 'Grampians', ['Horsham', 'Natimuk', 'Haven']],
  ['moorabool', 'Moorabool', 'Grampians', ['Bacchus Marsh', 'Ballan', 'Gordon']],
  ['northern-grampians', 'Northern Grampians', 'Grampians', ['Stawell', 'St Arnaud', 'Halls Gap']],
  ['pyrenees', 'Pyrenees', 'Grampians', ['Beaufort', 'Avoca', 'Snake Valley']],
  ['west-wimmera', 'West Wimmera', 'Grampians', ['Edenhope', 'Kaniva', 'Goroke']],
  ['yarriambiack', 'Yarriambiack', 'Grampians', ['Warracknabeal', 'Hopetoun', 'Murtoa']],

  // ---------------- Loddon Mallee (10) ----------------
  ['buloke', 'Buloke', 'Loddon Mallee', ['Birchip', 'Charlton', 'Wycheproof']],
  ['campaspe', 'Campaspe', 'Loddon Mallee', ['Echuca', 'Kyabram', 'Rochester']],
  ['central-goldfields', 'Central Goldfields', 'Loddon Mallee', ['Maryborough', 'Dunolly', 'Carisbrook']],
  ['gannawarra', 'Gannawarra', 'Loddon Mallee', ['Kerang', 'Cohuna', 'Leitchville']],
  ['bendigo', 'Bendigo', 'Loddon Mallee', ['Bendigo', 'Eaglehawk', 'Kangaroo Flat'], { homes: 35, savings: 1720, solar: 6.6, battery: 10 }],
  ['loddon', 'Loddon', 'Loddon Mallee', ['Wedderburn', 'Inglewood', 'Boort']],
  ['macedon-ranges', 'Macedon Ranges', 'Loddon Mallee', ['Gisborne', 'Kyneton', 'Woodend']],
  ['mildura', 'Mildura', 'Loddon Mallee', ['Mildura', 'Red Cliffs', 'Irymple']],
  ['mount-alexander', 'Mount Alexander', 'Loddon Mallee', ['Castlemaine', 'Maldon', 'Harcourt']],
  ['swan-hill', 'Swan Hill', 'Loddon Mallee', ['Swan Hill', 'Robinvale', 'Nyah']],

  // ---------------- North East Victoria (Hume region) (12) ----------------
  ['alpine', 'Alpine', 'North East Victoria', ['Bright', 'Myrtleford', 'Mount Beauty']],
  ['benalla', 'Benalla', 'North East Victoria', ['Benalla', 'Goorambat', 'Baddaginnie']],
  ['shepparton', 'Shepparton', 'North East Victoria', ['Shepparton', 'Mooroopna', 'Tatura'], { homes: 36, savings: 1740 }],
  ['indigo', 'Indigo', 'North East Victoria', ['Beechworth', 'Rutherglen', 'Yackandandah']],
  ['mansfield', 'Mansfield', 'North East Victoria', ['Mansfield', 'Merrijig', 'Jamieson']],
  ['mitchell', 'Mitchell', 'North East Victoria', ['Wallan', 'Seymour', 'Kilmore']],
  ['moira', 'Moira', 'North East Victoria', ['Cobram', 'Yarrawonga', 'Numurkah']],
  ['murrindindi', 'Murrindindi', 'North East Victoria', ['Alexandra', 'Yea', 'Kinglake']],
  ['strathbogie', 'Strathbogie', 'North East Victoria', ['Euroa', 'Nagambie', 'Violet Town']],
  ['towong', 'Towong', 'North East Victoria', ['Corryong', 'Tallangatta', 'Bellbridge']],
  ['wangaratta', 'Wangaratta', 'North East Victoria', ['Wangaratta', 'Glenrowan', 'Milawa']],
  ['wodonga', 'Wodonga', 'North East Victoria', ['Wodonga', 'Bandiana', 'Baranduda'], { homes: 36, savings: 1740, solar: 6.6, battery: 10 }],

  // ---------------- Gippsland (6) ----------------
  ['bass-coast', 'Bass Coast', 'Gippsland', ['Wonthaggi', 'Cowes', 'Inverloch']],
  ['baw-baw', 'Baw Baw', 'Gippsland', ['Warragul', 'Drouin', 'Trafalgar']],
  ['east-gippsland', 'East Gippsland', 'Gippsland', ['Bairnsdale', 'Lakes Entrance', 'Paynesville']],
  ['latrobe', 'Latrobe', 'Gippsland', ['Traralgon', 'Moe', 'Morwell']],
  ['south-gippsland', 'South Gippsland', 'Gippsland', ['Leongatha', 'Korumburra', 'Foster']],
  ['wellington', 'Wellington', 'Gippsland', ['Sale', 'Maffra', 'Yarram']],
];

export const AREAS = DEFS.reduce((acc, [slug, name, group, suburbs, overrides]) => {
  acc[slug] = makeArea(slug, name, group, suburbs, overrides);
  return acc;
}, {});

export const AREA_LIST = Object.values(AREAS);

// Areas grouped by region, in REGION_ORDER, for footer rendering
export const AREAS_BY_REGION = REGION_ORDER.map((region) => ({
  region,
  areas: AREA_LIST.filter((a) => a.group === region),
})).filter((g) => g.areas.length > 0);
